import React from "react";
import moment from "moment";
import { useNavigate, useParams } from "react-router-dom";
import { formatDate } from "../../helper/formatDate";
import ParentHeader from "../../Parent/ParentHeader/ParentHeader";
import { Msr, useMaterialSymbols, useOutside } from "./components";
import { getOireachtasEvent, getCompetitions, getRoster, getCart, getTeams, quoteCart, getStatement, getReceipts, getBalance, payBalance, getReference, getAddOns } from "./api";
import { parseMoneyCents, paymentAmountError, dueLabel } from "./instalments";
import FamilyStatement, { statementCsvRows, hasStatementContent } from "./FamilyStatement";
import compareComps from "./compSort";
import { buildFullEntry, fullEntryCsvRows, roundDanceText } from "./fullEntry";
import { regWindow } from "./deadlines";
import "./oireachtas.css";
import "./t1.css";

// T10 — current entries. Read-only roll-up of the school's paid entries (roster
// `entry`), registered teams (GET /:id/teams, M4), plus any unpaid draft (cart),
// grouped by dancer. Reconciled to the spec mockup
// teacher-registration-screens/10-current-entries.html. The settled-payment
// breakdown isn't exposed by the reads yet — see report.

const birthYear = (b) => {
  if (!b) return null;
  const y = moment(formatDate(b)).year();
  return Number.isFinite(y) ? y : null;
};
const money = (cents, cur) =>
  new Intl.NumberFormat("en-CA", { style: "currency", currency: cur || "CAD" }).format((cents || 0) / 100);

// "Pay in instalments" (card 15862648): one open instalment order — what the
// school owes, when it's due, and a box to pay any part of it now.
function BalanceCard({ order, onPay }) {
  const [amountText, setAmountText] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);
  // Payments still processing already count against the balance (the server
  // enforces the same), so only the rest can be paid now.
  const payableCents = Math.max(0, order.balanceCents - (order.pendingCents || 0));
  const cents = parseMoneyCents(amountText);
  const inputError = amountText ? paymentAmountError(cents, payableCents) : null;
  const submit = async () => {
    const err = paymentAmountError(cents, payableCents);
    if (err) { setError(err); return; }
    setBusy(true);
    setError(null);
    try {
      await onPay(order, cents);
    } catch (e) {
      // serviceConsumer rejects with { messages }; our own guard with an Error.
      setError(Array.isArray(e && e.messages) ? e.messages.join("; ") : (e && (e.messages || e.message)) || "Could not start the payment.");
      setBusy(false);
    }
  };
  return (
    <div className={"ce-balance" + (order.overdue ? " overdue" : "")}>
      <div className="bl-top">
        <span className="bl-ic"><Msr>account_balance_wallet</Msr></span>
        <div className="bl-txt">
          <div className="bl-h">
            Balance owing {money(order.balanceCents, order.currency)}
            {order.overdue && <span className="bl-over">Overdue</span>}
          </div>
          <div className="bl-s">
            Paid {money(order.paidCents, order.currency)} of {money(order.totalCents, order.currency)}
            {order.balanceDueAt ? ` · due ${dueLabel(order.balanceDueAt)}` : ""}
            {order.pendingCents > 0 ? ` · ${money(order.pendingCents, order.currency)} processing` : ""}
          </div>
        </div>
      </div>
      <div className="bl-pay">
        <div className="pc-input">
          <span>$</span>
          <input inputMode="decimal" placeholder={(payableCents / 100).toFixed(2)} value={amountText}
            disabled={payableCents <= 0}
            onChange={(e) => setAmountText(e.target.value)} aria-label="Amount to pay"
            aria-invalid={!!(error || inputError)}
            aria-describedby={error || inputError ? `bl-err-${order.order_id}` : undefined} />
        </div>
        <button type="button" className="bigbtn" disabled={busy || payableCents <= 0 || !cents || !!inputError} onClick={submit}>
          {busy ? "Starting payment…" : <><Msr>lock</Msr>Pay</>}
        </button>
      </div>
      {(error || inputError) && <div className="pc-err" id={`bl-err-${order.order_id}`} role="alert">{error || inputError}</div>}
      {payableCents <= 0 && !error && (
        <div className="pc-note"><Msr>hourglass_top</Msr>A payment is still processing — the rest can be paid once it clears.</div>
      )}
    </div>
  );
}

function Section({ icon, name, count, open, onToggle, children }) {
  return (
    <div className="ce-sec">
      <div className="ce-sec-h" onClick={onToggle}>
        <span className="ic"><Msr>{icon}</Msr></span>
        <span className="nm">{name}</span>
        <span className="ct">{count}</span>
        <span className={"chev" + (open ? " open" : "")}><Msr>expand_more</Msr></span>
      </div>
      {open && children}
    </div>
  );
}

function ExportMenu({ onCsv, onStatementCsv, onFullPdf }) {
  const [open, setOpen] = React.useState(false);
  const ref = useOutside(open, setOpen);
  return (
    <div className="exp" ref={ref}>
      <button type="button" className="expbtn" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <Msr>file_download</Msr>Export<Msr style={{ fontSize: 18 }}>expand_more</Msr>
      </button>
      {open && (
        <div className="exp-menu">
          <button type="button" className="exp-item" onClick={() => { setOpen(false); onCsv(); }}>
            <Msr>table_view</Msr>
            <span className="et">Download CSV<span className="es">All entries with set dances, figures and add-ons</span></span>
          </button>
          {onStatementCsv && (
            <button type="button" className="exp-item" onClick={() => { setOpen(false); onStatementCsv(); }}>
              <Msr>request_quote</Msr>
              <span className="et">Who owes what<span className="es">Per family, itemised by dancer</span></span>
            </button>
          )}
          {onFullPdf && (
            <button type="button" className="exp-item" onClick={() => { setOpen(false); onFullPdf(); }}>
              <Msr>fact_check</Msr>
              <span className="et">Full entry PDF<span className="es">Set dances, figures and add-ons</span></span>
            </button>
          )}
          <button type="button" className="exp-item" onClick={() => { setOpen(false); setTimeout(() => window.print(), 60); }}>
            <Msr>picture_as_pdf</Msr>
            <span className="et">Download PDF<span className="es">Print-ready summary of this view</span></span>
          </button>
        </div>
      )}
    </div>
  );
}

// The full entry (card 024af124): each dancer's competitions with the choices
// made for them, each team's dance and figure, and the add-ons bought — the
// details the family/competition views leave out. Draft rows are marked.
const choiceText = (e) =>
  [e.set && (e.speed ? `${e.set} · ${e.speed}` : e.set), roundDanceText(e)]
    .filter(Boolean)
    .join(" · ");

function FullEntry({ full, addOns, addOnsFailed }) {
  // Every item the event offers, 0 included — "no céilí tickets" is an answer too.
  const shownAddOns = addOns || [];
  if (!full.dancers.length && !full.teams.length && !shownAddOns.length && !addOnsFailed) {
    return <div className="ce-empty rc-none"><p>No entries yet.</p></div>;
  }
  return (
    <div className="fst fe">
      {full.dancers.length > 0 && <div className="fe-h">Solo entries</div>}
      {full.dancers.map((d) => (
        <div className="fst-fam" key={d.id}>
          <div className="fst-fh">
            <span className="nm">
              <Msr>person</Msr>{d.name}
              {d.age != null && <span className="cv-fam">U{d.age}</span>}
            </span>
            <span className="tot">
              <span className="count">{d.entries.length} entr{d.entries.length === 1 ? "y" : "ies"}</span>
            </span>
          </div>
          {d.entries.map((e) => (
            <div className="fst-line" key={e.compId}>
              <span className="k">
                {e.name}
                {e.code && <span className="cv-code">{e.code}</span>}
                {!e.paid && <span className="fst-pill unpaid">Draft</span>}
              </span>
              <span className="v fe-choice">{choiceText(e)}</span>
            </div>
          ))}
        </div>
      ))}

      {full.teams.length > 0 && <div className="fe-h">Teams</div>}
      {full.teams.map((t) => (
        <div className="fst-fam" key={t.id}>
          <div className="fst-fh">
            <span className="nm">
              <Msr>groups</Msr>{t.name}{t.team ? ` · ${t.team}` : ""}
              {t.code && <span className="cv-code">{t.code}</span>}
            </span>
            <span className="tot">
              <span className="count">{t.members.length} dancer{t.members.length === 1 ? "" : "s"}</span>
            </span>
          </div>
          {t.dance && (
            <div className="fst-line"><span className="k">Dance</span><span className="v fe-choice">{t.dance}</span></div>
          )}
          {t.figure && (
            <div className="fst-line"><span className="k">Figure</span><span className="v fe-choice">{t.figure}</span></div>
          )}
          <div className="fst-line">
            <span className="k">Members</span>
            <span className="v fe-choice">{t.members.map((m) => m.name).join(", ")}</span>
          </div>
        </div>
      ))}

      {addOnsFailed && (
        <>
          <div className="fe-h">Add-ons</div>
          <div className="fst-fam school" role="alert">
            <div className="fst-line muted">
              <span className="k"><Msr>error</Msr>Add-on counts (céilí tickets, ads) could not be loaded. Reload the page before sharing this entry.</span>
            </div>
          </div>
        </>
      )}
      {!addOnsFailed && shownAddOns.length > 0 && (
        <>
          <div className="fe-h">Add-ons</div>
          <div className="fst-fam school">
            <div className="fst-fh">
              <span className="nm"><Msr>confirmation_number</Msr>Paid add-ons</span>
            </div>
            {shownAddOns.map((a) => (
              <div className="fst-line" key={a.key}>
                <span className="k">{a.label}</span>
                <span className="v">{a.qty}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default function CurrentEntries() {
  useMaterialSymbols();
  const { id } = useParams();
  const navigate = useNavigate();
  const [state, setState] = React.useState({ loading: true });
  const [openSolo, setOpenSolo] = React.useState(true);
  const [openTeams, setOpenTeams] = React.useState(true);
  // "all" = the cumulative statement; a paymentId = that payment's frozen
  // receipt. Every add-entries -> pay sitting settles a new checkout, which
  // shows up here as one more tab.
  const [tab, setTab] = React.useState("all");
  // The All-entries tab reads two ways: "family" = who owes what (the
  // statement), "comp" = who is entered in what. Receipt tabs are bills, so
  // they stay family-grouped.
  const [view, setView] = React.useState("family");

  React.useEffect(() => {
    let alive = true;
    (async () => {
      const [evR, compR, rosR, cartR, qR, tmR, stR, rcR, blR, refR, aoR] = await Promise.allSettled([
        getOireachtasEvent(id), getCompetitions(id), getRoster(id, true), getCart(id), quoteCart(id), getTeams(id),
        getStatement(id), getReceipts(id), getBalance(id), getReference(), getAddOns(id),
      ]);
      if (!alive) return;
      if (evR.status !== "fulfilled") {
        setState({ loading: false, error: (evR.reason && evR.reason.messages) || "Event not found." });
        return;
      }
      setState({
        loading: false,
        event: evR.value,
        competitions: compR.status === "fulfilled" ? compR.value || [] : [],
        roster: rosR.status === "fulfilled" ? rosR.value || [] : [],
        cart: cartR.status === "fulfilled" ? cartR.value : null,
        quote: qR.status === "fulfilled" ? qR.value : null,
        teams: tmR.status === "fulfilled" ? tmR.value || [] : [],
        // Per-family statement. A failure here must not take the page down —
        // the entries roll-up above is still useful without it.
        statement: stR.status === "fulfilled" ? stR.value : null,
        // One frozen receipt per settled payment, newest first.
        receipts: rcR.status === "fulfilled" ? rcR.value || [] : [],
        // Instalment orders ("Pay in instalments"); failure just hides the card.
        orders: blR.status === "fulfilled" ? blR.value || [] : [],
        // Full-entry view: set/round-dance names, and the paid add-on counts.
        // Either failing leaves keys shown as stored / the add-ons card hidden.
        reference: refR.status === "fulfilled" ? refR.value : null,
        addOns: aoR.status === "fulfilled" ? aoR.value || [] : [],
        // A failed read must not pass for "none bought": the view and the
        // exports say the counts are missing instead (review R1-F2).
        addOnsFailed: aoR.status !== "fulfilled",
      });
    })();
    return () => { alive = false; };
  }, [id]);

  const model = React.useMemo(() => {
    if (!state.event) return null;
    const { event, competitions, roster, cart } = state;
    const compById = Object.fromEntries((competitions || []).map((c) => [String(c._id), c]));
    const eventDay = (event.eventDays && event.eventDays[0]) || event.entriesCloseDate;
    const eventYear = eventDay ? moment(formatDate(eventDay)).year() : new Date().getFullYear();

    const map = {};
    const ensure = (did, name) => (map[did] = map[did] || { id: did, name: name || "Dancer", age: null, comps: [], seen: new Set() });
    const pushComp = (did, cid) => {
      const c = compById[String(cid)];
      const code = c ? c.code : String(cid);
      if (map[did].seen.has(code)) return;
      map[did].seen.add(code);
      map[did].comps.push({ label: (c && (c.name || c.code)) || "Competition", code });
    };

    (roster || []).forEach((d) => {
      const did = String(d._id);
      const e = ensure(did, `${d.fname || ""} ${d.lname || ""}`.trim());
      const by = birthYear(d.birthday);
      e.age = by ? eventYear - by : null;
      if (d.entry && d.entry.competitions) d.entry.competitions.forEach((cid) => pushComp(did, cid));
    });
    (cart && cart.items ? cart.items : []).forEach((it) => {
      const did = String(it.dancer_id);
      ensure(did);
      (it.competitions || []).forEach((cid) => pushComp(did, cid));
    });

    const solo = Object.values(map).filter((d) => d.comps.length).sort((a, b) => a.name.localeCompare(b.name));
    const entriesCount = solo.reduce((n, d) => n + d.comps.length, 0);
    const paidCount = (roster || []).filter((d) => d.entry).length;
    const hasDraft = !!(cart && cart.items && cart.items.length) || !!(cart && cart.teams && cart.teams.length);
    const teams = state.teams || [];
    const ageOf = (b) => {
      const by = birthYear(b);
      return by ? eventYear - by : null;
    };
    const full = buildFullEntry({ roster, cart, teams, competitions, reference: state.reference, ageOf });
    return { solo, entriesCount, paidCount, hasDraft, teams, full };
  }, [state]);

  // The statement's entry lines regrouped by competition — "who is in what".
  // Line labels are the comp names, so the comp doc (when found) supplies the
  // syllabus ordering: type, then youngest band first, then girls before
  // mixed. Catalog array order is insertion order and interleaves groups, so
  // it is never used as-is.
  const compGroups = React.useMemo(() => {
    const stmt =
      state.statement && (state.statement.families.length || state.statement.unlinked)
        ? state.statement
        : null;
    if (!stmt) return [];
    const byName = Object.fromEntries((state.competitions || []).map((c) => [c.name, c]));
    const buckets = new Map();
    const walk = (fam, famLabel) => {
      (fam.dancers || []).forEach((d) =>
        (d.lines || []).forEach((l) => {
          if (!buckets.has(l.label)) buckets.set(l.label, []);
          buckets.get(l.label).push({ dancer: d.name, family: famLabel });
        })
      );
    };
    stmt.families.forEach((f) => walk(f, f.label));
    if (stmt.unlinked) walk(stmt.unlinked, "No family linked");
    // Syllabus order via the canonical comparator (compSort.js — verified
    // against our own competition list, seed/ecro2026Template.js). A label
    // with no matching comp doc sorts after everything, alphabetically.
    return [...buckets.entries()]
      .map(([label, rows]) => ({
        label,
        code: (byName[label] || {}).code,
        comp: byName[label] || null,
        rows: rows.sort((a, b) => a.dancer.localeCompare(b.dancer)),
      }))
      .sort((a, b) => {
        if (a.comp && b.comp) return compareComps(a.comp, b.comp);
        if (a.comp) return -1;
        if (b.comp) return 1;
        return a.label.localeCompare(b.label);
      });
  }, [state]);

  if (state.loading) return <div className="oir"><ParentHeader /><div className="loading">Loading entries…</div></div>;
  if (state.error) return <div className="oir"><ParentHeader /><div className="loading">{state.error}</div></div>;

  const { event } = state;
  const school = state.roster.length && state.roster[0].schoolName; // schoolName not yet exposed by /roster — see report
  const schoolLabel = school || "Your school";
  const { solo, entriesCount, teams, full } = model;

  // The statement IS the entries list — family -> dancer -> that dancer's
  // competitions, priced, with team seats sitting under each member. It
  // supersedes the old by-dancer roll-up and the by-fee-type payment summary,
  // which between them said the same thing three times. The roll-up survives
  // only as a fallback for when the statement read fails.
  // hasStatementContent counts school-only charges too (an add-on-only
  // payment has no families), so such a statement still renders.
  const st = hasStatementContent(state.statement) ? state.statement : null;
  const dancerCount = st
    ? st.families.reduce((n, f) => n + f.dancers.length, 0) +
      (st.unlinked ? st.unlinked.dancers.length : 0)
    : solo.length;
  const familyCount = st ? st.families.length + (st.unlinked ? 1 : 0) : 0;
  const paidAddOns = (state.addOns || []).some((a) => a.qty > 0);
  const hasAnything = !!st || solo.length > 0 || teams.length > 0 || paidAddOns;
  const paidCents = st ? st.totals.paidCents : 0;
  // The statement is SETTLED money only, so an in-progress draft has to come
  // from the quote — otherwise the header would read "Paid" while the teacher
  // still has unpaid work sitting in the cart.
  const hasUnpaidDraft = !!(state.quote && state.quote.breakdown && state.quote.breakdown.totalCents > 0);
  // Instalment orders with money still owing (card 15862648).
  const owingOrders = (state.orders || []).filter((o) => o.balanceCents > 0);

  // Start one balance payment and hand off to the shared Stripe page.
  const startBalancePayment = async (order, amountCents) => {
    const resp = await payBalance(id, order.order_id, amountCents);
    const clientSecret = resp.checkout && resp.checkout.client_secret;
    if (!clientSecret) throw new Error("Could not start the payment.");
    navigate("/checkout", {
      state: {
        from: "oireachtasReg",
        oireachtasId: id,
        paymentDetails: {
          showPaymentPage: true, client: clientSecret, errors: "",
          amountCents: resp.amountCents, kind: "balance",
          balanceAfterCents: resp.balanceAfterCents, balanceDueAt: order.balanceDueAt,
        },
        eventDetails: {
          eventType: "Oireachtas",
          eventName: event && event.name,
          eventDate: event && event.eventDays && event.eventDays[0],
          eventImage: event && event.logo,
        },
      },
    });
  };

  const save = (rows, suffix) => {
    const esc = (s) => '"' + String(s).replace(/"/g, '""') + '"';
    const csv = rows.map((r) => r.map(esc).join(",")).join("\r\n");
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(event.name || "Oireachtas").replace(/[^\w]+/g, "_")}_${suffix}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  // Every entry with its choices (set, speed, round-2 dance, team dance and
  // figure), one row per team MEMBER, then the add-on counts — see fullEntry.js.
  const downloadCSV = () =>
    save(fullEntryCsvRows(full, state.addOns, { addOnsFailed: state.addOnsFailed }), "entries");

  // The full entry is its own view; switch to it, then print once it renders.
  const printFullEntry = () => {
    setTab("all");
    setView("full");
    setTimeout(() => window.print(), 150);
  };

  // What each family owes, one row per line item so it sorts in a spreadsheet.
  const downloadStatementCSV = () =>
    save(statementCsvRows(state.statement, event.currency), "who_owes_what");

  // A single past payment, exactly as it was issued.
  const downloadReceiptCSV = (r) =>
    save(
      statementCsvRows(r.statement, r.currency || event.currency),
      "receipt_" + (r.receiptNumber || r.paymentId)
    );

  // Receipt tabs run oldest -> newest so they read as sittings: first payment,
  // second payment, … The API sorts newest first for list use.
  const rcTabs = [...(state.receipts || [])].reverse();
  const activeReceipt = rcTabs.find((r) => r.paymentId === tab) || null;
  const showAll = tab === "all" || !activeReceipt;
  const when = (d) =>
    new Date(d).toLocaleDateString("en-CA", { year: "numeric", month: "short", day: "numeric" });

  return (
    <div className="oir">
      <ParentHeader />
      <div className="wrap narrow ce-wrap">
        <button type="button" className="back" onClick={() => navigate(`/oireachtas/${id}`)}>
          <Msr>arrow_back</Msr>Back to event
        </button>

        <div className="ce-ph">
          {event.logo && <img className="logo" src={event.logo} alt="" />}
          <div className="ph-txt">
            <h1>{event.name} — Current entries</h1>
            <div className="sub">
              <span className="sch"><Msr>school</Msr>{schoolLabel}</span>
              {paidCents > 0 && !hasUnpaidDraft && !owingOrders.length && (
                <span className="paid-pill"><Msr>check_circle</Msr>Paid</span>
              )}
              {owingOrders.length > 0 && (
                <span className="pill draft"><Msr>account_balance_wallet</Msr>Balance owing</span>
              )}
              {hasUnpaidDraft && <span className="pill draft"><Msr>edit_note</Msr>Draft in progress</span>}
            </div>
          </div>
          {hasAnything && (
            <div className="head-actions">
              <ExportMenu
                onCsv={downloadCSV}
                onFullPdf={printFullEntry}
                onStatementCsv={state.statement && state.statement.families.length ? downloadStatementCSV : null}
              />
              {regWindow(event).state !== "closed" ? (
                <button type="button" className="bigbtn" onClick={() => navigate(`/teacher/oireachtas/${id}/register`)}>
                  <Msr>add</Msr>Add entries
                </button>
              ) : (
                <span className="closed-note"><Msr>lock</Msr>Entries closed</span>
              )}
            </div>
          )}
        </div>

        {owingOrders.map((o) => (
          <BalanceCard key={o.order_id} order={o} onPay={startBalancePayment} />
        ))}

        {!hasAnything ? (
          <div className="ce-empty">
            <div className="em-ic"><Msr>group_add</Msr></div>
            <h3>No entries yet</h3>
            <p>Once you register dancers for {event.name}, their competitions will be listed here.</p>
            <button type="button" className="bigbtn" onClick={() => navigate(`/teacher/oireachtas/${id}/register`)}>
              <Msr>group_add</Msr>Register dancers
            </button>
          </div>
        ) : (
          <>
            <div className="ce-summary">
              <div className="fig"><span className="n">{dancerCount}</span><span className="lab">Dancers</span></div>
              <div className="fig"><span className="n">{familyCount}</span><span className="lab">Families</span></div>
            </div>

            {/* One tab per settled payment; "All entries" is the roll-up. */}
            {rcTabs.length > 0 && (
              <div className="ce-tabs" role="tablist">
                <button
                  type="button" role="tab" aria-selected={showAll}
                  className={"ce-tab" + (showAll ? " active" : "")}
                  onClick={() => setTab("all")}
                >
                  <Msr>list_alt</Msr>All entries
                </button>
                {rcTabs.map((r) => {
                  const active = !showAll && r.paymentId === tab;
                  return (
                    <button
                      type="button" role="tab" aria-selected={active}
                      className={"ce-tab" + (active ? " active" : "")}
                      key={r.paymentId}
                      onClick={() => setTab(r.paymentId)}
                    >
                      <Msr>receipt_long</Msr>
                      {r.receiptNumber ? `Receipt #${r.receiptNumber}` : "Payment"}
                      <span className="sub">{when(r.paidAt)}</span>
                    </button>
                  );
                })}
              </div>
            )}

            {!showAll && activeReceipt && (
              /* One payment, exactly as issued — frozen, never recomputed. */
              <>
                {activeReceipt.statement ? (
                  <FamilyStatement
                    statement={activeReceipt.statement}
                    currency={activeReceipt.currency || event.currency}
                  />
                ) : (
                  <div className="ce-empty rc-none">
                    <p>This payment predates itemised receipts; only its total is available.</p>
                  </div>
                )}
                <div className="ce-pay">
                  <div className="pay-h">
                    <span className="ic"><Msr>receipt_long</Msr></span>
                    <span className="nm">
                      {activeReceipt.receiptNumber ? `Receipt #${activeReceipt.receiptNumber}` : "Payment"}
                    </span>
                    <span className="ro"><Msr>lock</Msr>Paid {when(activeReceipt.paidAt)}</span>
                  </div>
                  <div className="pay-body">
                    <div className="ce-prow total">
                      <span className="k">Payment total</span>
                      <span className="v">
                        {money(activeReceipt.totalCents, activeReceipt.currency || event.currency)}
                      </span>
                    </div>
                    {activeReceipt.statement && (
                      <div className="ce-prow rc-dlrow">
                        <span className="k">This receipt, as issued</span>
                        <button type="button" className="rc-dl" onClick={() => downloadReceiptCSV(activeReceipt)}>
                          <Msr>file_download</Msr>CSV
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}

            {/* Two readings of the same settled money: by family (who owes
                what) or by competition (who is entered in what). */}
            {showAll && (
              <div className="ce-views" role="group" aria-label="Group entries by">
                <button
                  type="button"
                  className={"vt" + (view === "family" ? " on" : "")}
                  onClick={() => setView("family")}
                >
                  <Msr>diversity_3</Msr>Family
                </button>
                {compGroups.length > 0 && (
                  <button
                    type="button"
                    className={"vt" + (view === "comp" ? " on" : "")}
                    onClick={() => setView("comp")}
                  >
                    <Msr>emoji_events</Msr>Competition
                  </button>
                )}
                <button
                  type="button"
                  className={"vt" + (view === "full" ? " on" : "")}
                  onClick={() => setView("full")}
                >
                  <Msr>fact_check</Msr>Full entry
                </button>
              </div>
            )}

            {showAll && view === "full" && (
              <FullEntry full={full} addOns={state.addOns} addOnsFailed={state.addOnsFailed} />
            )}

            {showAll && st && view === "comp" && compGroups.length > 0 && (
              <div className="fst">
                {compGroups.map((g) => (
                  <div className="fst-fam" key={g.label}>
                    <div className="fst-fh">
                      <span className="nm">
                        {g.label}
                        {g.code && <span className="cv-code">{g.code}</span>}
                      </span>
                      <span className="tot">
                        <span className="count">
                          {g.rows.length} dancer{g.rows.length === 1 ? "" : "s"}
                        </span>
                      </span>
                    </div>
                    {g.rows.map((r, i) => (
                      <div className="fst-line" key={i}>
                        <span className="k">
                          {r.dancer}
                          <span className="cv-fam">{r.family}</span>
                        </span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}

            {/* ONE list: family -> dancer -> that dancer's competitions, priced.
                Team dances sit under each member with their own seat fee, so a
                team is never a separate section to cross-reference. */}
            {showAll && (view === "family" || (view === "comp" && !compGroups.length)) && (st ? (
              <>
                <FamilyStatement statement={st} currency={event.currency} />
                <div className="ce-pay">
                  <div className="pay-h">
                    <span className="ic"><Msr>receipt_long</Msr></span>
                    <span className="nm">Total</span>
                    <span className="ro"><Msr>lock</Msr>Settled payments</span>
                  </div>
                  <div className="pay-body">
                    <div className="ce-prow">
                      <span className="k">Families</span>
                      <span className="v">{money(st.totals.familiesCents, event.currency)}</span>
                    </div>
                    <div className="ce-prow">
                      <span className="k">School charges</span>
                      <span className="v">{money(st.totals.schoolCents, event.currency)}</span>
                    </div>
                    <div className="ce-prow total">
                      <span className="k">Total</span>
                      <span className="v">
                        {money(st.totals.grandTotalCents, event.currency)}
                      </span>
                    </div>
                  </div>
                </div>

              </>
            ) : (
              /* Statement unavailable (a failed read) — fall back to the roll-up
                 so the page still tells the teacher who is registered. Teams are
                 listed separately here because without the statement there is
                 nothing else carrying them: a team-only school would otherwise
                 see an empty page. */
              <>
                {!solo.length && !teams.length && (
                  <div className="ce-empty rc-none">
                    <p>No dancer entries. Paid add-ons are listed under Full entry.</p>
                  </div>
                )}
                {solo.length > 0 && (
                  <Section icon="person" name="Entries"
                    count={`${solo.length} dancer${solo.length === 1 ? "" : "s"} · ${entriesCount} entr${entriesCount === 1 ? "y" : "ies"}`}
                    open={openSolo} onToggle={() => setOpenSolo((v) => !v)}>
                    {solo.map((d) => (
                      <div className="srow" key={d.id}>
                        <div className="who">
                          <span className="nm">{d.name}</span>
                          {d.age != null && <span className="ag">· U{d.age}</span>}
                        </div>
                        <div className="comps">
                          {d.comps.map((c) => (
                            <span className="ce-chip" key={c.code}>{c.label}<span className="code">{c.code}</span></span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </Section>
                )}

                {teams.length > 0 && (
                  <Section icon="groups" name="Teams"
                    count={`${teams.length} team${teams.length === 1 ? "" : "s"}`}
                    open={openTeams} onToggle={() => setOpenTeams((v) => !v)}>
                    {teams.map((t) => {
                      const c = t.competition || {};
                      return (
                        <div className="srow" key={String(t._id)}>
                          <div className="who">
                            <span className="nm">{c.name || c.code || "Team"}{t.teamLetter ? ` · Team ${t.teamLetter}` : ""}</span>
                            {(t.danceName || t.figure) && (
                              <span className="ag">· {t.danceName}{t.figure ? ` — ${t.figure}` : ""}</span>
                            )}
                          </div>
                          <div className="comps">
                            {(t.members || []).map((m, j) => (
                              <span className="ce-chip" key={j}>{m.fname} {m.lname}</span>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </Section>
                )}
              </>
            ))}
          </>
        )}
      </div>
    </div>
  );
}
